/**
 * Interact tab (screenshots/79): what a click on a mark does (filter the overview · open the page · drill down), hover
 * (tooltip, emphasis, dim others to 35%), and motion (enter, live updates, duration, easing). Reduced motion turns all of
 * it into a 100 ms fade (applySettings + reduced-motion.css).
 */
import { CIcon } from "../icons";
import { Dropdown } from "../../../components/shared/Dropdown";
import { DATASETS, type ChartSettings, type InteractionSettings } from "../chartSettings";
import { useChartSettings, type ChartSettingsStore } from "../chartSettingsStore";
import { Switch } from "../../../components/shared/ui";
import { Row, Section, Seg } from "./ui";

export const EASING_LABEL: Record<InteractionSettings["easing"], string> = { cubicOut: "Standard · cubic-out", quarticOut: "Emphasized · quartic-out", linear: "Linear" };
/** The noun for one mark of this type ("Click a bar"). */
export const markNoun = (type: ChartSettings["type"]) => (type === "bar" || type === "column" || type === "diverging" ? "bar" : type === "wardMap" ? "ward" : type === "donut" ? "slice" : "mark");

/** What a click opens, from the category field's type. */
function pageOf(s: ChartSettings): { noun: string; path: string } | null {
  const f = DATASETS[s.data.datasetId].fields.find((x) => x.id === s.data.mapping.category);
  if (!f) return null;
  if (f.type === "person") return { noun: s.data.datasetId === "contributions" || f.id === "member" || f.id === "sponsor" ? "alderperson" : "person", path: "/people/{personId}" };
  if (f.type === "org") return { noun: "organization", path: "/organizations/{clientId}" };
  if (f.type === "ward") return { noun: "ward", path: "/people?ward={ward}" };
  if (f.id === "record" || f.id === "matter" || f.id === "item") return { noun: "legislation", path: "/legislation/{recordId}" };
  return null;
}

export function InteractTab({ store, alsoFilters }: { store: ChartSettingsStore; alsoFilters: { title: string; icon: string }[] }) {
  const s = useChartSettings(store, (x) => x.settings);
  const it = s.interact;
  const set = (patch: Partial<InteractionSettings>, label: string) => store.getState().update((x) => ({ ...x, interact: { ...x.interact, ...patch } }), label);
  const ds = DATASETS[s.data.datasetId];
  const cat = ds.fields.find((f) => f.id === s.data.mapping.category);
  const catNoun = cat ? (cat.type === "person" && pageOf(s)?.noun === "alderperson" ? "Alderperson" : cat.label.charAt(0).toUpperCase() + cat.label.slice(1)) : "this field";
  const page = pageOf(s);
  const drillTo = it.drillTo ?? ds.fields.find((f) => f.type === "date" && f.id !== s.data.mapping.category)?.id;
  const then = ds.fields.find((f) => f.type === "category" && f.id !== s.data.mapping.category)?.label;
  const options: { id: InteractionSettings["click"]; icon: string; title: string; sub: string }[] = [
    { id: "filter", icon: "funnel", title: "Filter the overview", sub: `Other widgets that use ${catNoun} follow along` },
    ...(page ? [{ id: "open" as const, icon: "arrow-up-right", title: `Open the ${page.noun} page`, sub: page.path }] : []),
    ...(drillTo ? [{ id: "drill" as const, icon: "layers", title: "Drill down", sub: `Into ${ds.label} by ${drillTo}${then ? `, then by ${then.replace(/ type$/, "")}` : ""}` }] : []),
  ];
  const zoomable = (s.type === "line" || s.type === "area" || s.type === "column");

  return (
    <div className="cs-sections">
      <Section title={`Click a ${markNoun(s.type)}`}>
        <div className="cs-options" role="radiogroup" aria-label={`Click a ${markNoun(s.type)}`}
          onKeyDown={(e) => {
            const d = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : e.key === "ArrowUp" || e.key === "ArrowLeft" ? -1 : 0;
            if (!d) return;
            e.preventDefault();
            const i = Math.max(0, options.findIndex((o) => o.id === it.click));
            const n = options[(i + d + options.length) % options.length];
            set({ click: n.id, drillTo: n.id === "drill" ? drillTo : it.drillTo }, `Click: ${n.title}`);
            (e.currentTarget.children[options.indexOf(n)] as HTMLElement | undefined)?.focus();
          }}>
          {options.map((o) => (
            <button key={o.id} type="button" role="radio" aria-checked={it.click === o.id} tabIndex={it.click === o.id || (!options.some((x) => x.id === it.click) && o === options[0]) ? 0 : -1}
              className={`cs-option${it.click === o.id ? " is-on" : ""}`} onClick={() => set({ click: o.id, drillTo: o.id === "drill" ? drillTo : it.drillTo }, `Click: ${o.title}`)}>
              <CIcon name={o.icon} size={16} /><span><strong>{o.title}</strong><small>{o.sub}</small></span>
              <span className="cs-option__radio" aria-hidden>{it.click === o.id && <CIcon name="check" size={11} />}</span>
            </button>
          ))}
        </div>
        {it.click === "filter" && alsoFilters.length > 0 && (
          <div className="cs-also"><span>Also filters:</span>{alsoFilters.map((w) => <span key={w.title} className="cs-pill cs-pill--static"><CIcon name={w.icon} />{w.title}</span>)}</div>
        )}
      </Section>

      <Section title="Hover">
        <Row label="Tooltip"><Switch on={it.tooltip} label="Tooltip" onChange={(tooltip) => set({ tooltip }, tooltip ? "Tooltip on" : "Tooltip off")} /><span className="cs-row__muted">Uses the template from Labels</span></Row>
        <Row label="Emphasis">
          <Seg label="Emphasis" value={it.emphasis} onChange={(emphasis) => set({ emphasis }, "Emphasis")}
            options={[{ id: "self", label: `This ${markNoun(s.type)}` }, { id: "series", label: "Its series" }, { id: "none", label: "None" }]} />
        </Row>
        <Row label="Dim others"><Switch on={it.dimOthers} label="Dim others" onChange={(dimOthers) => set({ dimOthers }, "Dim others")} /><span className="cs-row__muted">to 35%</span></Row>
      </Section>

      <Section title="Motion">
        <Row label="When added">
          <Seg label="When added" value={it.enter} onChange={(enter) => set({ enter }, "Enter motion")} options={[{ id: "grow", label: "Grow" }, { id: "fade", label: "Fade" }, { id: "none", label: "None" }]} />
        </Row>
        <Row label="Live updates">
          <Seg label="Live updates" value={it.update} onChange={(update) => set({ update }, "Update motion")} options={[{ id: "morph", label: "Morph" }, { id: "flash", label: "Flash change" }, { id: "none", label: "None" }]} />
        </Row>
        <Row label="Duration">
          <input className="cs-range" type="range" min={0} max={800} step={50} value={it.duration} aria-label="Duration" aria-valuetext={`${it.duration} ms`}
            style={{ ["--p" as string]: `${(it.duration / 800) * 100}%` }} onChange={(e) => set({ duration: Number(e.target.value) }, "Duration")} />
          <output className="cs-range__out">{it.duration} ms</output>
        </Row>
        <Row label="Easing">
          <Dropdown className="cs-select" label="Easing" icon={<CIcon name="spline" />} value={it.easing} onChange={(easing) => set({ easing }, "Easing")} chevron={<CIcon name="chevron-down" className="cs-select__chev" />}
            options={(Object.keys(EASING_LABEL) as InteractionSettings["easing"][]).map((k) => ({ value: k, label: EASING_LABEL[k] }))} />
        </Row>
        <p className="cs-note cs-note--icon"><CIcon name="accessibility" size={12} />Reduced motion: values swap with a 100 ms fade, nothing grows or moves.</p>
      </Section>

      <Section title={<span className={zoomable ? undefined : "is-dim"}>Zoom &amp; brush</span>}>
        {zoomable && (
          <div className="cs-row cs-row--switches">
            <Switch on={!!it.zoom} label="Zoom & brush" onChange={(zoom) => set({ zoom }, zoom ? "Zoom & brush on" : "Zoom & brush off")} />
            <span className="cs-row__text">Zoom &amp; brush</span>
          </div>
        )}
        <p className={`cs-note cs-note--icon${zoomable ? "" : " is-dim"}`}><CIcon name="zoom-in" size={12} />Available on Line, Area and Column over time</p>
      </Section>
    </div>
  );
}
